import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../utils/constants';
import {
  getApiBase,
  hasFallback,
  isLastFallback,
  switchToNextFallback,
} from './endpointManager';
import { refreshSession, ON_401_MIN_INTERVAL_MS, clearRefreshMark } from './sessionRefresh';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
  // 認證改用 httpOnly cookie（JS 讀不到 token，防 XSS 竊取）；每次請求自動帶上
  withCredentials: true,
});

// 請求攔截器：動態 baseURL（認證憑證由 httpOnly cookie 自動攜帶，不再手動塞 Bearer）
api.interceptors.request.use((config) => {
  // EndpointManager 決定主要或備援
  const base = getApiBase();
  if (base) {
    config.baseURL = `${base}/api`;
  }
  return config;
});

// 回應攔截器：401 先試著用 refresh token 續期，真的不行才登出；
//              網路故障則切換備援
let isRedirecting = false;

/** 續期一律走 `sessionRefresh` —— 它同時擋三種對撞：同 context 並行、跨分頁、以及
 *  「剛剛才續過」的多餘輪替。
 *
 *  原本這裡自己有一把鎖，但只鎖得住本檔的 401 路徑；`useTokenRefresh` 的定時續期
 *  走 authService 另一條路，兩條互不知道 ⇒ 仍會同一個權杖送兩次而被重用偵測撤銷整組。
 *  2026-11-02 實際在 `auth_refresh_tokens` 看到（9 天 13 次），故把鎖移到共用模組。 */

function forceLogout() {
  localStorage.removeItem('authed');
  localStorage.removeItem('user');
  clearRefreshMark();   // 下次登入的第一次續期不該被冷卻窗吃掉
  if (!isRedirecting && window.location.pathname !== '/login') {
    isRedirecting = true;
    window.location.href = '/login';
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const cfg = error.config as (InternalAxiosRequestConfig & { _refreshRetried?: boolean }) | undefined;

    if (error.response?.status === 401) {
      const url = cfg?.url || '';
      // 登入／續期本身回 401 就是真的不行，不能再遞迴續期
      const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login');

      if (!isAuthEndpoint && cfg && !cfg._refreshRetried) {
        cfg._refreshRetried = true;
        if (await refreshSession(ON_401_MIN_INTERVAL_MS)) {
          return api(cfg);          // 續期成功 → 原請求重試一次
        }
      }
      forceLogout();
      return Promise.reject(error);
    }

    // 網路錯誤 or 超時 → 嘗試備援
    const isNetworkError = !error.response && (
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNABORTED' ||
      error.message?.includes('Network Error') ||
      error.message?.includes('timeout')
    );

    const config = error.config as InternalAxiosRequestConfig & { _failoverRetried?: boolean };

    if (isNetworkError && hasFallback() && config && !config._failoverRetried) {
      config._failoverRetried = true;

      if (!isLastFallback()) {
        switchToNextFallback();
      }

      // 用下一層備援端點重送
      const base = getApiBase();
      config.baseURL = `${base}/api`;
      return api.request(config);
    }

    return Promise.reject(error);
  }
);

export default api;
